import axios, { type AxiosError } from "axios"
import { captureException } from "@sentry/nextjs"

import { createAuth } from "./auth"
import { API_ROOT_URL, SELF_RECORDING_APP_URL } from "../requests"
import { appDispatch } from "../lib/store"
import type { OtpChallenge, OtpIdentifier, Participant } from "@/types/api-types"

export const LOGIN = "LOGIN"

type FailedCallback = (status: number | string) => void

type LoginSucceededArgs = {
  token: string
  user: Participant
  insightsEnabled: boolean | undefined
}

const onLoginSucceeded = ({ token, user, insightsEnabled }: LoginSucceededArgs) => {
  appDispatch({ type: "loginAt", payload: Date.now() })
  appDispatch(createAuth(user, token))
  if (insightsEnabled) {
    axios.get(`${SELF_RECORDING_APP_URL}/insights/sync`).catch((err) => {
      console.error(err)
    })
  }
}

const onLoginFailed = (err: AxiosError, failedCallback: FailedCallback) => {
  if (err.response && err.response.data) {
    failedCallback(err.response.status)
  } else {
    captureException(err)
    failedCallback("")
  }
}

export function initiateLogin(
  phone: string,
  isSendAgain: boolean,
  successCallback: (challenge: OtpChallenge) => void,
  failedCallback: FailedCallback,
): void {
  const params = { phone, isSendAgain }
  axios
    .post<OtpChallenge>(`${API_ROOT_URL}/public/login/initiate`, params)
    .then(({ data: identifier }) => successCallback(identifier))
    .catch((err: AxiosError) => {
      onLoginFailed(err, failedCallback)
    })
}

export const completeLogin = async (
  identifier: OtpIdentifier | undefined,
  oneTimePassword: string,
  loginCallback: () => void | Promise<void>,
  failedCallback: FailedCallback,
  insightsEnabled: boolean | undefined,
): Promise<void> => {
  return verifyOtpAndFetchParticipant(
    "public/login/complete",
    identifier,
    oneTimePassword,
    loginCallback,
    failedCallback,
    insightsEnabled,
  )
}

export const completeSignup = async (
  identifier: OtpIdentifier | undefined,
  oneTimePassword: string,
  loginCallback: () => void | Promise<void>,
  failedCallback: FailedCallback,
  insightsEnabled: boolean | undefined,
): Promise<void> => {
  return verifyOtpAndFetchParticipant(
    "public/signup/complete",
    identifier,
    oneTimePassword,
    loginCallback,
    failedCallback,
    insightsEnabled,
  )
}

const verifyOtpAndFetchParticipant = async (
  action: string,
  identifier: OtpIdentifier | undefined,
  oneTimePassword: string,
  loginCallback: () => void | Promise<void>,
  failedCallback: FailedCallback,
  insightsEnabled: boolean | undefined,
): Promise<void> => {
  if (!identifier) return
  const data = { oneTimePassword, ...identifier }
  try {
    const { data: token } = await axios.post<string>(`${API_ROOT_URL}/${action}`, data)
    const { data: user } = await axios.get<Participant>(`${API_ROOT_URL}/protected/participant`, {
      headers: { Authorization: token },
    })
    onLoginSucceeded({ token, user, insightsEnabled })
    await loginCallback()
  } catch (err) {
    onLoginFailed(err as AxiosError, failedCallback)
  }
}
